import React from 'react';
import { Checkbox, Input } from '@spark';
import { HelpIcon } from '../shared/TaskTypeControls';
import ToolSourceCard from './ToolSourceCard';
import ToolsFoundList from './ToolsFoundList';
import DiscoveryLinkField from './DiscoveryLinkField';
import { useSourceDiscovery } from './useSourceDiscovery';

const MCP_LINK_HELP =
    'URL of an MCP server (Streamable HTTP or SSE). Its tools are listed under "Tools Selected".';
const MCP_EXECUTION_HELP = 'MCP server the trained agent calls at inference time to run the tools.';
/** Figma 4012:17891 / 4012:18112 placeholder copy. */
export const MCP_LINK_PLACEHOLDER = 'e.g. "url": "https://tools.companyname.ai/mcp"';
export const MCP_EXECUTION_PLACEHOLDER = 'https://tools.companyname2.ai/mcp';

/**
 * "MCP" card (Figma 4012:17891 / 4012:18112): MCP Link (w-440) with live
 * discovery, "Use same MCP for tool execution" checkbox (checked by default,
 * which hides the execution field) and, when unchecked, the
 * "MCP Link for Tool Execution *" input (w-400) in the right column.
 */
const McpToolSourceCard = ({ source, errors = {}, discovery, onChange, onRemove }) => {
    const { runDiscovery, toggleTool, setSelectedTools, loading, discoveryError } = useSourceDiscovery({
        source,
        discovery,
        onChange,
    });

    return (
        <ToolSourceCard type={source.type} onRemove={onRemove}>
            <div className="cw-ac-mcp__row">
                <div className="cw-ac-mcp__discovery">
                    <DiscoveryLinkField
                        label="MCP Link"
                        helpText={MCP_LINK_HELP}
                        placeholder={MCP_LINK_PLACEHOLDER}
                        value={source.discovery_url}
                        onChange={(next) => onChange({ discovery_url: next })}
                        onDiscover={runDiscovery}
                        error={errors.discovery_url}
                        disabled={loading}
                        className="cw-ac-link-field--w440"
                    />
                    <Checkbox
                        label="Use same MCP for tool execution"
                        status={source.use_same_mcp_for_execution ? 'active' : 'inactive'}
                        onChange={(e) => onChange({ use_same_mcp_for_execution: e.target.checked })}
                        className="cw-ac-mcp__same-checkbox"
                    />
                </div>
                {!source.use_same_mcp_for_execution && (
                    <div className="cw-ac-mcp__execution">
                        <div className="cw-section-label">
                            MCP Link for Tool Execution
                            <span className="cw-required-asterisk">*</span>
                            <HelpIcon text={MCP_EXECUTION_HELP} />
                        </div>
                        <Input
                            size="medium"
                            showLabel={false}
                            showHelperText={false}
                            placeholder={MCP_EXECUTION_PLACEHOLDER}
                            aria-label="MCP Link for Tool Execution"
                            value={source.execution_url}
                            danger={Boolean(errors.execution_url)}
                            onChange={(e) => onChange({ execution_url: e.target.value })}
                            className="cw-ac-mcp__execution-input"
                        />
                        {errors.execution_url && (
                            <div className="cw-field-error">{errors.execution_url}</div>
                        )}
                    </div>
                )}
            </div>
            <ToolsFoundList
                tools={source.tools}
                selectedTools={source.selected_tools}
                onToggle={toggleTool}
                onSelectedChange={setSelectedTools}
                loading={loading}
                error={discoveryError || errors.tools}
            />
        </ToolSourceCard>
    );
};

export default McpToolSourceCard;
